import React, { Fragment, useRef, useEffect } from 'react';
import { useTheme, createUseStyles } from 'react-jss';
import { motion } from 'framer-motion';
import Tile from './Tile';
import Robot from './Robot';
import CircleIcon from '../icons/CircleIcon';
import TriangleIcon from '../icons/TriangleIcon';
import SquareIcon from '../icons/SquareIcon';
import StarIcon from '../icons/StarIcon';
import UltimateIcon from '../icons/UltimateIcon';

const SHAPE_ICONS = {
  circle: CircleIcon,
  triangle: TriangleIcon,
  square: SquareIcon,
  star: StarIcon,
  special: UltimateIcon,
};
const DIMENSION = 16;

function buildTiles(boardState, highlightedTiles, activeMission, walls, missions) {
  const highlightSet = new Set(highlightedTiles.map(t => `${t.x},${t.y}`));
  const tiles = [];

  for (let y = 0; y < DIMENSION; y++) {
    const row = [];
    for (let x = 0; x < DIMENSION; x++) {
      const w = walls[`${x},${y}`] ?? {};
      const isCenter = (x === 7 || x === 8) && (y === 7 || y === 8);
      const missionAtTile = missions.find(m => m.x === x && m.y === y);
      const isActive = activeMission && activeMission.x === x && activeMission.y === y;
      row.push({
        x, y,
        upperWall: w.top ?? false,
        rightWall: w.right ?? false,
        bottomWall: w.bottom ?? false,
        leftWall: w.left ?? false,
        possibleDestination: highlightSet.has(`${x},${y}`),
        isCenter,
        mission: missionAtTile ? { color: missionAtTile.color, shape: missionAtTile.shape, active: isActive } : null,
        key: `${y}${x}`,
      });
    }
    tiles.push(row);
  }
  return tiles;
}

const useStyles = createUseStyles({
  frame: {
    // Tightened by the mobile layout, which may be in force on a screen far
    // wider than a phone — hence a prop rather than a media query.
    padding: ({ compact }) => compact ? '4px' : '10px',
    borderRadius: '12px',
    background: ({ theme }) => theme.colorFrame,
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.14)',
      'inset 0 -2px 0 rgba(0,0,0,0.35)',
      '0 10px 30px rgba(0,0,0,0.35)',
    ].join(','),
    boxSizing: 'border-box',
    // The handoff specifies `width: fit-content`, but the grid's max-content
    // width is ~0 (the tiles are empty divs), so fit-content collapses the
    // board. Carrying the board's existing `width: 100%` + maxWidth here
    // instead keeps the exact sizing behaviour and still bounds the frame.
    width: '100%',
    // Desktop: `boardArea` now pads 24px a side, so the cap has to subtract
    // that back out (48px, plus 8px for the frame's own drop shadow) instead
    // of fighting it — otherwise the frame is squeezed by the gutter rather
    // than sitting inside it. `100%` is the width the flex row leaves once
    // the sidebar has taken its share. Compact keeps the old cap: the mobile
    // gutter is 4px and the bottom bar owns the rest.
    maxWidth: ({ compact }) => compact
      ? 'min(99vw, 99vh)'
      : 'min(100%, calc(100dvh - 56px))',
  },
  container: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: `repeat(${DIMENSION}, 1fr)`,
    backgroundColor: ({ theme }) => theme.colorPrimary,
    overflow: 'hidden',
    borderRadius: '5px',
    boxShadow: 'inset 0 0 0 1px rgba(42,26,14,0.45), inset 0 3px 8px rgba(42,26,14,0.18)',
    aspectRatio: '1',
    width: '100%',
    touchAction: 'manipulation',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
  },
  trailSvg: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 1,
  },
  robotSlot: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'auto',
    cursor: 'pointer',
  },
  // The 2x2 centre used to be four tiles painted `colorCenter` and nothing
  // else, which reads as a hole punched through the board. One stamped,
  // radiused plate reads as a part of it. It sits under `centerMission`
  // (zIndex 2), which occupies the same four cells.
  centerPlate: {
    position: 'absolute',
    left: `${7/16 * 100}%`,
    top: `${7/16 * 100}%`,
    width: `${2/16 * 100}%`,
    height: `${2/16 * 100}%`,
    borderRadius: '8px',
    background: ({ theme }) => theme.colorCenter,
    boxShadow: [
      'inset 0 0 0 1px rgba(245,230,200,0.10)',   // hairline lip
      'inset 0 2px 0 rgba(0,0,0,0.45)',           // pressed top edge
      'inset 0 -1px 0 rgba(245,230,200,0.07)',    // catch-light at the bottom
      '0 1px 0 rgba(245,230,200,0.06)',           // sits proud of the tiles
    ].join(','),
    zIndex: 1,
    pointerEvents: 'none',
  },
  centerMission: {
    position: 'absolute',
    left: `${7/16 * 100}%`,
    top: `${7/16 * 100}%`,
    width: `${2/16 * 100}%`,
    height: `${2/16 * 100}%`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
    zIndex: 2,
    pointerEvents: 'none',
  },
  missionIconWrap: {
    width: '55%',
    height: 'auto',
    aspectRatio: '1',
    filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.3))',
    animation: '$pulse 2s ease-in-out infinite',
  },
  missionLabel: {
    color: '#f5e6c8',
    fontSize: 'clamp(7px, 1.8vw, 13px)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    textAlign: 'center',
    lineHeight: 1.1,
    textShadow: '0 0 8px rgba(0,0,0,0.8)',
  },
  '@keyframes pulse': {
    '0%, 100%': { transform: 'scale(1)', opacity: 0.85 },
    '50%': { transform: 'scale(1.1)', opacity: 1 },
  },
});

function Board({ boardState, walls, missions, highlightedTiles, selectedRobot, activeMission, robotTrails, onRobotSelect, onTileClick, compact }) {
  const theme = useTheme();
  const classes = useStyles({ theme, compact });
  const prevPositions = useRef({});

  const tiles = buildTiles(boardState ?? {}, highlightedTiles ?? [], activeMission, walls ?? {}, missions ?? []);

  useEffect(() => {
    prevPositions.current = boardState ?? {};
  }, [boardState]);

  return (
    <div className={classes.frame}>
      <div className={classes.container}>
        {tiles.map((row, index) => (
          <Fragment key={index}>
            {row.map(tile => (
              <Tile
                tile={tile}
                key={tile.key}
                onTileClick={onTileClick}
                selectedColor={selectedRobot}
              />
            ))}
          </Fragment>
        ))}

        <svg className={classes.trailSvg} viewBox="0 0 16 16" preserveAspectRatio="none">
          {Object.entries(robotTrails ?? {}).map(([color, points]) => {
            if (points.length < 2) return null;
            return (
              <polyline
                key={color}
                points={points.map(p => `${p.x + 0.5},${p.y + 0.5}`).join(' ')}
                fill="none"
                stroke={color}
                strokeWidth={0.12}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={0.7}
              />
            );
          })}
        </svg>

        <div className={classes.centerPlate} />

        {activeMission && (() => {
          const Icon = SHAPE_ICONS[activeMission.shape] ?? UltimateIcon;
          const colorLabel = activeMission.color === 'any' ? 'Any' : activeMission.name.split('-')[0];
          return (
            <div className={classes.centerMission}>
              <div className={classes.missionIconWrap}>
                <Icon color={activeMission.color === 'any' ? '#fff' : activeMission.color} />
              </div>
              <div className={classes.missionLabel}>{colorLabel}</div>
            </div>
          );
        })()}

        <div className={classes.overlay}>
          {Object.entries(boardState ?? {}).map(([color, pos]) => {
            const prev = prevPositions.current[color];
            const dist = prev ? Math.abs(pos.x - prev.x) + Math.abs(pos.y - prev.y) : 0;

            const transition = dist > 0
              ? { type: 'tween', duration: 0.1 + dist * 0.05, ease: 'easeInOut' }
              : { duration: 0 };

            return (
              <motion.div
                key={color}
                className={classes.robotSlot}
                style={{
                  width: `${100 / DIMENSION}%`,
                  height: `${100 / DIMENSION}%`,
                }}
                // Mount where the robot already is, and animate only from
                // there on. Without this the first render has nothing to
                // animate from, so no `left` is written at all and the robot
                // falls back to the grid origin — every robot stacked in the
                // top-left corner until something moves it.
                initial={false}
                animate={{
                  top: `${(pos.y / DIMENSION) * 100}%`,
                  left: `${(pos.x / DIMENSION) * 100}%`,
                }}
                transition={transition}
                onClick={() => onRobotSelect(color)}
              >
                <Robot robot={{ color, selected: color === selectedRobot }} />
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Board;
